import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  TextField,
  Typography
} from '@material-ui/core';
import { unwrapResult } from '@reduxjs/toolkit';
import clsx from 'clsx';
import { cloneDeep, uniq } from 'lodash';
import { Page } from 'src/components';
import { CategoriesEnum, ProductTypeEnum } from 'src/enums';
import { slices, useAppDispatch } from 'src/redux';
import {
  GetTransactionDetailResponse,
  GetTransactionPaymentsResponse,
  GetTransactionsViaTransactionNoResponse,
  Transaction,
  TransactionPrintPageData
} from 'src/types';
import {
  formatDate,
  getNumberOrZero,
  initializedWordsOtherThanFirst,
  isEmptyOrSpaces,
  sleep
} from 'src/utils';
import { Document, pdf } from '@react-pdf/renderer';
import { RenderPages } from '../TransactionPDF/components/RenderPages';
import { useBranchInfo } from 'src/hooks/branch/use-branch-info';
import { getMostCommonBranchIdOnTransactionItems } from 'src/redux/slices/branches/util';
import { transactionDataCleaner } from '../TransactionPDF/util';
import { saveAs } from 'file-saver';

const { actions: transactionActions } = slices.transaction;
const { actions: transactionPaymentActions } = slices.transactionPayments;

type UrlObject = {
  transactionNo: string;
  orderNo: string;
  isDownloaded: boolean;
  message?: string;
};

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3),
    PointerEvent: 'none'
  },
  customerInfoBox: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3)
  },

  printButton: {
    marginBottom: 14
  },
  extraInfo: {
    margin: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end'
  },
  deleteBtn: {
    marginTop: 20
  },
  cardContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  }
}));

const TransactionInfoViewPrint = () => {
  const { branchIDToName } = useBranchInfo();
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const [rawInput, setRawInput] = useState<string>('');
  const [urlObjects, setUrlObjects] = useState<UrlObject[]>([]);

  const handleTransform = () => {
    const lines = rawInput
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    const unique = Array.from(new Set(lines)); // Remove duplicate URLs

    const parsedObjects: UrlObject[] = unique
      .map((url) => {
        const transactionMatch = url.match(/transaction\/(PCW-\d+)/);
        const orderNoMatch = url.match(/\?on=(\d+)/);

        return {
          transactionNo: transactionMatch?.[1] || '',
          orderNo: orderNoMatch?.[1] || '',
          isDownloaded: false
        };
      })
      .filter((obj) => obj.transactionNo && obj.orderNo); // remove invalid rows

    setUrlObjects(parsedObjects);
    console.log(parsedObjects);
  };

  const handleSingleDownload = async (index: number) => {
    const urlObject = urlObjects[index];
    console.log(urlObject);

    // Getting Transaction Info
    const transactionInfoRes = unwrapResult(
      await dispatch(
        transactionActions.getTransactionViaTransactionNo(
          urlObject?.transactionNo
        )
      )
    );
    if (!transactionInfoRes?.success) {
      const err = {
        success: false,
        message: 'Failed to fetch transaction info. Skipping download.'
      };
      return err;
    }

    const transactionInfoForDownload: GetTransactionsViaTransactionNoResponse =
      transactionInfoRes?.originalData;

    // Get Transaction Details
    const transactionDetailRes = unwrapResult(
      await dispatch(
        transactionActions.getTransactionDetailThunk(urlObject?.transactionNo)
      )
    );
    if (!transactionDetailRes?.success) {
      const err = {
        success: false,
        message: 'Failed to fetch transaction details. Skipping download.'
      };
      return err;
    }
    const transactionDetailsForDownload: GetTransactionDetailResponse =
      transactionDetailRes?.originalData;

    const getPaymentsRes = unwrapResult(
      await dispatch(
        transactionPaymentActions.getTransactionPaymentsThunk(
          urlObject?.transactionNo
        )
      )
    );

    let transactionPaymentResponseForDownload:
      | GetTransactionPaymentsResponse
      | undefined = getPaymentsRes?.originalData || undefined;

    const mappedTerms = transactionPaymentResponseForDownload?.data?.map(
      (x) => {
        if (x?.deleted_by) {
          return '';
        }
        return x?.payment_type || '';
      }
    );

    let isReceiptSRPByDefaultForDownload = false;
    const uniqTerms: string[] = uniq(mappedTerms)?.filter((x) => x !== '');
    const termsWithSRP = ['PAYMONGO'];
    const hasSRPTerm = uniqTerms?.some((r) => termsWithSRP?.indexOf(r) >= 0);

    if (hasSRPTerm && transactionDetailsForDownload?.data?.ecomm_order_ref_no) {
      isReceiptSRPByDefaultForDownload = true;
    }

    let transactionInfoWithOrderNo = {
      ...transactionInfoForDownload,
      order_no: urlObject?.orderNo || '',
      isSRPByDefault: isReceiptSRPByDefaultForDownload,
      store_address: transactionDetailsForDownload?.data?.store_address,
      ec_order_id: transactionDetailsForDownload?.data?.ec_order_id,
      payment_mode: uniqTerms?.join(', ')
    };
    transactionInfoWithOrderNo.items = transactionInfoWithOrderNo?.items?.filter(
      (x) => x.isDeleted === 0
    );

    const receiptItemsOrder = [
      CategoriesEnum.GPU,
      CategoriesEnum.CPU,
      CategoriesEnum.Motherboard,
      CategoriesEnum.RAM,
      CategoriesEnum.SSD,
      CategoriesEnum.HDD,
      CategoriesEnum.PSU,
      CategoriesEnum.Casing,
      CategoriesEnum.Monitor
    ];

    transactionInfoWithOrderNo?.items?.sort(function(a, b) {
      if (receiptItemsOrder?.indexOf(a?.category_id || 0) <= -1) {
        return 1;
      }
      if (receiptItemsOrder?.indexOf(b?.category_id || 0) <= -1) {
        return -1;
      }
      return (
        receiptItemsOrder.indexOf(a?.category_id || 0) -
        receiptItemsOrder.indexOf(b?.category_id || 0)
      );
    });

    console.log(transactionInfoWithOrderNo);

    const fileName = `${transactionInfoWithOrderNo?.order_no}`;
    const blob = await pdf(
      <RenderDocumentForAutoDownload
        transactionInfoWithOrderNo={transactionInfoWithOrderNo}
      />
    ).toBlob();
    saveAs(blob, fileName);

    const success = {
      success: true,
      message: `Downloaded transaction ${urlObject?.transactionNo} successfully.`
    };
    return success;
  };

  const downloadAll = async () => {
    for (let index = 0; index < urlObjects.length; index++) {
      const result = await handleSingleDownload(index);
      await sleep(1000);

      setUrlObjects((prev) =>
        prev.map((item, idx) =>
          idx === index
            ? {
                ...item,
                isDownloaded: !!result?.success,
                message: result?.message
              }
            : item
        )
      );

      if (!result?.success) {
        console.error(`Failed to download transaction ${index + 1}`);
      } else {
        console.log(`Downloaded transaction ${index + 1} successfully.`);
      }
    }

    console.log('All downloads processed.');
  };

  const RenderDocumentForAutoDownload = ({
    transactionInfoWithOrderNo
  }: {
    transactionInfoWithOrderNo: any;
  }) => {
    const storeBranch = () => {
      const commonBranchName = branchIDToName(
        getMostCommonBranchIdOnTransactionItems(
          transactionInfoWithOrderNo?.items
        )
      );
      return commonBranchName || '';
    };

    const transactionItems = () => {
      let clonedTransactionStateData = cloneDeep(transactionInfoWithOrderNo);
      if (clonedTransactionStateData) {
        clonedTransactionStateData.items = clonedTransactionStateData.items?.filter(
          (x: any) => x?.rma?.is_hidden_print === false || !x?.rma
        );
      }
      const arr: Transaction[] = clonedTransactionStateData?.items || [];
      const reducedItems: Transaction[] = [];
      arr.forEach((item) => {
        const existingIndex = reducedItems?.findIndex(
          (x) =>
            x?.product_name == item?.product_name &&
            // also check price because some products are same but have different retails price
            x?.retail_price === item?.retail_price
        );
        if (existingIndex > -1) {
          reducedItems[existingIndex].quantity =
            (reducedItems[existingIndex].quantity || 0) + (item?.quantity || 0);
          reducedItems[existingIndex].amount =
            (reducedItems[existingIndex].amount || 0) + (item?.amount || 0);

          if (item?.serial_no) {
            reducedItems[existingIndex].serial_nos?.push(item?.serial_no);
          }
        } else {
          if (item.serial_no) {
            item.serial_nos = [item.serial_no];
          }
          reducedItems.push(item);
        }
      });
      return reducedItems;
    };

    const pagesData = () => {
      const limitOfLinePerPage = 22; // hard coded optimal number by manually counting lines before overflow occurs
      let totalLinesAccumulated = 0;

      let pageData: TransactionPrintPageData[] = [
        {
          pageNo: 1,
          page: {
            ...transactionInfoWithOrderNo,
            items: []
          }
        }
      ];

      const clonedTransactionItems = cloneDeep(transactionItems());

      clonedTransactionItems.forEach((i, ind) => {
        const name = i.product_name || '';
        const characterPerLine = 43; // no. of characters the productname cell can fit before wrapping to next line
        const filteredSerial =
          i.serial_nos?.filter((serial) => {
            // check serial conditions
            const isValidSerial =
              i?.product_type !== ProductTypeEnum.Consumable &&
              i?.serial_nos &&
              i?.serial_nos?.length > 0;
            // do not allow non serialized items
            const IsSerializeditem =
              !/^SN-\d{13}$/?.test(serial) && !serial?.includes('PCWSN-');

            const serialConditions = [isValidSerial, IsSerializeditem];

            return serialConditions.every((condition) => !!condition);
          }) || [];

        const linesTakenBySerial = filteredSerial.length || 0;
        const pageNoBasedOnLength = pageData?.length;
        const lastIndexOfPageData = pageNoBasedOnLength - 1;
        const isLastPage = ind === clonedTransactionItems.length - 1;
        const nameLength = name.length || 0;
        const linesTakenByName = Math.ceil(nameLength / characterPerLine);
        const linesTakeByWarranty = 1;
        let linesTakenByItem =
          linesTakenByName + linesTakeByWarranty + linesTakenBySerial;

        if (transactionInfoWithOrderNo?.kachi_points !== 0 && isLastPage) {
          linesTakenByItem++; // add kachi points to line consideration if its the last item
        }

        if (transactionInfoWithOrderNo?.voucher_discount !== 0 && isLastPage) {
          linesTakenByItem++;
        }

        const updatedItem = { ...i, serial_nos: filteredSerial };

        if (totalLinesAccumulated + linesTakenByItem <= limitOfLinePerPage) {
          // @ts-ignore: Object is possibly 'null'. Intended.
          pageData[lastIndexOfPageData]?.page.items.push(updatedItem);
          totalLinesAccumulated += linesTakenByItem;
        } else {
          totalLinesAccumulated = 0; // reset total lines val and re add new one
          totalLinesAccumulated += linesTakenByItem;
          pageData.push({
            pageNo: pageNoBasedOnLength + 1,
            page: {
              ...transactionInfoWithOrderNo,
              items: [updatedItem]
            }
          });
        }
      });

      const finalData = transactionDataCleaner(pageData);
      return finalData;
    };

    const dateSold = () => {
      if (
        transactionInfoWithOrderNo?.items &&
        transactionInfoWithOrderNo?.items?.length > 0
      ) {
        if (transactionInfoWithOrderNo?.items[0]?.created_at) {
          return formatDate(transactionInfoWithOrderNo?.items[0].created_at);
        }
        return '--';
      }
      return '--';
    };

    const totalPriceOnPrint = () => {
      const totalKachiPoints = Math.abs(
        transactionInfoWithOrderNo?.kachi_points || 0
      );
      const totalVoucherDiscount = Math.abs(
        transactionInfoWithOrderNo?.voucher_discount || 0
      );
      const total = transactionInfoWithOrderNo?.items?.reduce(
        (accumulator: any, item: any) => {
          let toBeAdded: number = 0;
          if (
            item?.rma?.is_hidden_print ||
            (item?.rma && item?.rma?.is_hidden_print === undefined)
          ) {
            toBeAdded = 0;
          } else {
            toBeAdded = getNumberOrZero(item?.amount);
          }
          return accumulator + toBeAdded;
        },
        0
      );
      const newTotal = (total || 0) - totalKachiPoints - totalVoucherDiscount;

      return newTotal;
    };

    const releasedBy = () => {
      const elementWithName = transactionInfoWithOrderNo?.items?.find(
        (x: any) => typeof x.released_by_name !== 'undefined'
      );
      // const arrName = elementWithName?.released_by_name;
      let firstAndInitialLastName = initializedWordsOtherThanFirst(
        elementWithName?.released_by_name || ''
      );
      if (isEmptyOrSpaces(firstAndInitialLastName)) {
        firstAndInitialLastName = 'PC Worth';
      }
      return `${elementWithName?.created_by || 0}-${firstAndInitialLastName}`;
    };

    return (
      <Document title={`${transactionInfoWithOrderNo?.order_no}`}>
        <RenderPages
          overrideOnURL={transactionInfoWithOrderNo?.order_no}
          storeBranch={storeBranch()}
          pagesData={pagesData()}
          transactionStateData={transactionInfoWithOrderNo}
          dateSold={dateSold()}
          totalAmount={totalPriceOnPrint()}
          releasedBy={releasedBy()}
        />
      </Document>
    );
  };

  return (
    <Page title="Transaction Details" className={clsx(classes.root)}>
      <Container maxWidth={false}>
        <h1>Accounting Dept Printing Services</h1>
        <TextField
          style={{ marginTop: 20 }}
          label="Paste URLs here"
          placeholder="One URL per line"
          multiline
          fullWidth
          minRows={10}
          value={rawInput}
          onChange={(e) => setRawInput(e.target.value)}
          variant="outlined"
        />

        <Button
          onClick={handleTransform}
          style={{ marginTop: 20 }}
          color="primary"
          variant="outlined"
        >
          Transform Links
        </Button>

        {urlObjects.length > 0 && (
          <Box mt={3}>
            <Typography variant="h6" gutterBottom>
              Transformed URLs
            </Typography>
            <Button
              onClick={() => downloadAll()}
              style={{ marginTop: 20 }}
              color="primary"
              variant="contained"
            >
              Download !!!
            </Button>
            <List>
              {urlObjects.map((obj, index) => (
                <div key={index}>
                  <ListItem style={{ gap: 2 }}>
                    <ListItemText
                      primary={`Order No: ${obj.orderNo} ${
                        obj?.message ? ` - ${obj?.message}` : ''
                      }`}
                      secondary={`Transaction No: ${obj.transactionNo}`}
                    />
                    <Chip
                      size="small"
                      label={obj.isDownloaded ? 'Downloaded' : 'Not Downloaded'}
                      color={obj.isDownloaded ? 'primary' : 'secondary'}
                    />
                  </ListItem>
                  {index < urlObjects.length - 1 && <Divider component="li" />}
                </div>
              ))}
            </List>
          </Box>
        )}
      </Container>
    </Page>
  );
};

export default TransactionInfoViewPrint;
